import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../../utils/api';
import Manicure from '../../assets/manicure4.png'
import Manicure1 from '../../assets/manicure5.jpeg'
import Manicure2 from '../../assets/manicure6.jpeg'

// ✅ ISOLATED Hero Slider Component with i18n
const HeroSlider = React.memo(() => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [currentSlide, setCurrentSlide] = useState(0);

  const heroSlides = [
    {
      id: 1,
      title: t('home.heroTitle1'),
      subtitle: t('home.heroSubtitle1'),
      image: Manicure,
      cta: t('home.heroCta')
    },
    {
      id: 2,
      title: t('home.heroTitle2'),
      subtitle: t('home.heroSubtitle2'),
      image: Manicure1,
      cta: t('home.heroCta')
    },
    {
      id: 3,
      title: t('home.heroTitle3'),
      subtitle: t('home.heroSubtitle3'),
      image: Manicure2,
      cta: t('home.heroCta')
    }
  ];

  // Auto-slide
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [heroSlides.length]);

  return (
    <div style={{ position: 'relative', height: 'clamp(100vh, 95vh, 95vh)', overflow: 'hidden' }}>
      {heroSlides.map((slide, index) => (
        <div key={slide.id} style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          opacity: currentSlide === index ? 1 : 0,
          transition: 'opacity 0.8s ease-in-out',
          background: `linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.6)), url(${slide.image})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
        }}>
          {/* Content Container */}
          <div style={{ 
            padding: 'clamp(2rem, 5vw, 4rem)',
            maxWidth: '600px',
            width: '100%'
          }}>
            {/* Title */}
            <h1 style={{ 
              fontSize: 'clamp(2.5rem, 8vw, 5rem)', 
              marginBottom: 'clamp(1rem, 3vw, 2rem)',
              fontWeight: '300',
              lineHeight: '1.1',
              color: '#fff',
              letterSpacing: '0.05em',
              textTransform: 'uppercase'
            }}>
              {slide.title}
            </h1>

            {/* Subtitle */}
            <p style={{
              fontSize: 'clamp(1rem, 2.5vw, 1.250rem)',
              lineHeight: '1.6',
              color: '#fff',
              marginBottom: 'clamp(2rem, 4vw, 3rem)',
              maxWidth: '500px',
              fontWeight: '300'
            }}>
              {slide.subtitle}
            </p>

            {/* Button */}
            <button 
              onClick={() => navigate('/shop')}
              style={{
                background: '#fff',
                border: 'none',
                padding: 'clamp(0.75rem, 2vw, 1rem) clamp(1.5rem, 4vw, 2.5rem)',
                borderRadius: '0',
                color: '#000',
                fontWeight: '600',
                fontSize: 'clamp(0.75rem, 1.5vw, 0.875rem)',
                cursor: 'pointer',
                transition: 'all 0.3s',
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                boxShadow: 'none'
              }} 
              onMouseEnter={(e) => {
                e.target.style.background = '#000';
                e.target.style.color = '#fff';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = '#fff';
                e.target.style.color = '#000';
              }}
            >
              {slide.cta}
            </button>
          </div>
        </div>
      ))}

      {/* Dots */}
      <div style={{ 
        position: 'absolute', 
        bottom: 'clamp(1rem, 3vw, 2rem)', 
        left: '50%', 
        transform: 'translateX(-50%)', 
        display: 'flex', 
        gap: '0.5rem',
        zIndex: 10
      }}>
        {heroSlides.map((_, index) => (
          <button 
            key={index} 
            onClick={() => setCurrentSlide(index)} 
            aria-label={`Go to slide ${index + 1}`}
            style={{
              width: currentSlide === index ? '30px' : '10px',
              height: '10px',
              borderRadius: '5px',
              border: 'none',
              background: currentSlide === index ? '#fff' : 'rgba(255, 255, 255, 0.5)',
              cursor: 'pointer',
              transition: 'all 0.3s'
            }} 
          />
        ))}
      </div>
    </div>
  );
});

const CarWheelHomePage = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [productsByCategory, setProductsByCategory] = useState({});

  useEffect(() => {
    fetchCategoriesAndProducts();
  }, [i18n.language]);

  const fetchCategoriesAndProducts = async () => {
    setLoading(true);
    try {
      const categoriesResponse = await api.get('/products/categories');
      
      const categoriesList = categoriesResponse.data.data || [];
      const firstThreeCategories = categoriesList.slice(0, 3);
      setCategories(firstThreeCategories);

      const productsResponse = await api.get('/products');
      const allProducts = productsResponse.data.data || [];

      // Group products by category
      const grouped = {};
      firstThreeCategories.forEach(category => {
        grouped[category.id] = allProducts.filter(
          product => product.category_id === category.id
        ).slice(0, 8);
      });

      setProductsByCategory(grouped);

    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Get image URL
  const getImageUrl = (imagePath) => {
    if (!imagePath) return Manicure;
    if (imagePath.startsWith('http')) return imagePath;
    return `https://kid-o.ge${imagePath}`;
  };

  // ✅ FIXED: Simple Product Card Component with FIXED height
  const SimpleProductCard = ({ product }) => {
    return (
      <div style={{
        backgroundColor: '#fff',
        borderRadius: '12px',
        overflow: 'hidden',
        cursor: 'pointer',
        transition: 'transform 0.3s, box-shadow 0.3s',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        width: '100%', // ✅ Take full width of container
        height: '100%', // ✅ FIXED: Full height
        display: 'flex', // ✅ FIXED: Flexbox layout
        flexDirection: 'column' // ✅ FIXED: Column direction
      }}
      onClick={() => navigate(`/product/${product.slug || product.id}`)}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-5px)';
        e.currentTarget.style.boxShadow = '0 10px 30px rgba(222, 172, 186, 0.3)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
      }}
      >
        {/* ✅ FIXED: Image with fixed height */}
        <div style={{
          width: '100%',
          height: '250px', // ✅ FIXED: Consistent height
          backgroundColor: '#f5f5f5',
          position: 'relative',
          flexShrink: 0 // ✅ FIXED: Don't shrink
        }}>
          <img 
            src={getImageUrl(product.primary_image || product.image_url)} 
            alt={product.name}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover' // ✅ FIXED: Cover maintains aspect ratio
            }}
            onError={(e) => {
              e.target.src = Manicure;
            }}
          />
        </div>

        {/* ✅ FIXED: Content with flex-grow to fill remaining space */}
        <div style={{ 
          padding: '1.5rem',
          display: 'flex',
          flexDirection: 'column',
          flexGrow: 1, // ✅ FIXED: Grow to fill space
          justifyContent: 'space-around' // ✅ FIXED: Space between elements
        }}>
          <h3 style={{
            fontSize: '1.1rem',
            fontWeight: '600',
            color: '#333',
            marginBottom: '0.5rem',
            height: 'auto', // ✅ FIXED: Fixed height for title
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical'
          }}>
            {product.name}
          </h3>
          
          <div>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: '0rem'
            }}>
              <span style={{
                fontSize: '1.5rem',
                fontWeight: 'bold',
                color: '#deacbaff'
              }}>
                ₾{parseFloat(product.price).toFixed(2)}
              </span>
            </div>

            <div style={{
              marginTop: '0.75rem',
              fontSize: '0.85rem',
              color: product.stock > 0 ? '#4ade80' : '#ef4444'
            }}>
              {product.stock > 0 ? ` ${t('product.inStock')}` : t('product.outOfStock')}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ✅ FIXED: Product Section Component with grid layout
  const ProductSection = ({ title, description, products, categorySlug }) => {
    if (!products || products.length === 0) {
      return null;
    }

    return (
      <div style={{ 
        padding: '4rem 0',
        backgroundColor: '#fff'
      }}>
        {/* Section Title + Description */}
        <div style={{ 
          textAlign: 'center', 
          marginBottom: '3rem',
          padding: '0 1rem'
        }}>
          <h2 style={{
            fontSize: 'clamp(2rem, 5vw, 3rem)',
            fontWeight: '300',
            color: '#deacbaff',
            marginBottom: '0.5rem'
          }}>
            {title}
          </h2>
          <div style={{
            width: '60px',
            height: '3px',
            background: 'linear-gradient(90deg, #deacbaff, #cbabb3ff)',
            margin: '0 auto',
            marginBottom: '1rem'
          }} />
          {description && (
            <p style={{
              fontSize: 'clamp(0.9rem, 2vw, 1rem)',
              color: '#828282ff',
              maxWidth: '600px',
              margin: '0 auto',
              lineHeight: '1.6',
              fontWeight: '300'
            }}>
              {description}
            </p>
          )}
        </div>

        {/* ✅ FIXED: Products Grid Container */}
        <div style={{
          maxWidth: '1400px',
          margin: '0 auto',
          paddingLeft: '1rem',
          paddingRight: '1rem'
        }}>
          <div 
            className="products-grid"
          >
            {products.map((product) => (
              <div 
                key={product.id}
                style={{
                  height: '420px' // ✅ FIXED: Consistent card height
                }}
              >
                <SimpleProductCard product={product} />
              </div>
            ))}
          </div>
        </div>

        {/* See All Button */}
        <div style={{ 
          textAlign: 'center', 
          marginTop: '3rem',
          padding: '0 1rem'
        }}>
          <button
            onClick={() => navigate(`/shop${categorySlug ? `?category=${categorySlug}` : ''}`)}
            style={{
              background: 'transparent',
              border: '2px solid #deacbaff',
              padding: '0.875rem 2.5rem',
              borderRadius: '30px',
              color: '#deacbaff',
              fontWeight: '600',
              fontSize: '1rem',
              cursor: 'pointer',
              transition: 'all 0.3s',
            }}
            onMouseEnter={(e) => {
              e.target.style.background = '#deacbaff';
              e.target.style.color = '#fff';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = 'transparent';
              e.target.style.color = '#deacbaff';
            }}
          >
            {t('home.seeAll')} →
          </button>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#fff'
      }}>
        <div style={{
          fontSize: '2rem',
          color: '#deacbaff'
        }}>
          {t('home.loading')}
        </div>
      </div>
    );
  }

  return (
    <div style={{ 
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      backgroundColor: '#fff',
      color: '#333',
      minHeight: '100vh'
    }}>
      {/* ✅ FIXED: Updated styles with grid layout */}
      <style>{`
        .products-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 1rem;
          margin-bottom: 3rem;
        }

        @media (min-width: 576px) {
          .products-grid {
            grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
            gap: 1.5rem;
          }
        }

        @media (min-width: 768px) {
          .products-grid {
            grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
            gap: 2rem;
          }
        }

        @media (min-width: 1024px) {
          .products-grid {
            grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
            gap: 2rem;
          }
        }

        @media (min-width: 1200px) {
          .products-grid {
            grid-template-columns: repeat(4, 1fr);
          }
        }
      `}</style>

      {/* Hero Slider */}
      <HeroSlider />

      {/* Display categories with descriptions */}
      {categories.map((category) => {
        const categoryProducts = productsByCategory[category.id];
        if (!categoryProducts || categoryProducts.length === 0) return null;

        return (
          <ProductSection 
            key={category.id}
            title={category.name}
            description={category.description}
            products={categoryProducts}
            categorySlug={category.slug}
          />
        );
      })}

      {/* If no categories, show message */}
      {categories.length === 0 && (
        <div style={{
          textAlign: 'center',
          padding: '4rem 2rem',
          color: '#999'
        }}>
          <p style={{ fontSize: '1.5rem' }}>{t('home.loading')}</p>
        </div>
      )}
    </div>
  );
};

export default CarWheelHomePage;